import React, { useEffect, useMemo, useState } from "react";
import FaiTypography from "MuiComponents/FaiTypography";
import theme from "assets/theme";
import dataTableData from "./data/dataTableData";
import { Box, Card, CircularProgress } from "@mui/material";
import SearchInput from "components/SearchInput";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import Axios from "services/api/config";
import { getAllOrdersURL } from "services/api/routes/Order";
import OrderDetails from "./component/OrderDetails";

interface OrderRow {
	id: string;
	orderId: string;
	productName: string;
	mode: string;
	mrp: string;
	status: string;
	orderDate: string;
	raw: any;
}

// Flatten a backend SubOrderResponseDTO into a flat table row.
const toRow = (so: any): OrderRow => ({
	id: so?.id,
	orderId: so?.razorpayOrderId || so?.id || "-",
	productName:
		(so?.items || [])
			.map((it: any) => it?.product?.productName)
			.filter(Boolean)
			.join(", ") || "-",
	mode: so?.paymentMode || "-",
	mrp: `₹ ${so?.totalPrice ?? 0}`,
	status: so?.status || "-",
	orderDate: so?.createdAt
		? new Date(so.createdAt).toLocaleDateString()
		: "-",
	raw: so,
});

function Orders() {
	const { typography } = theme;
	const [orderData, setOrderDetails] = useState<OrderRow | undefined>();
	const [orders, setOrders] = useState<OrderRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [search, setSearch] = useState("");

	const getOrders = async () => {
		try {
			setLoading(true);
			const { data } = await Axios({
				...getAllOrdersURL({
					page: paginationModel.page,
					size: paginationModel.pageSize,
				}),
			});
			const responseData = data?.data || {};
			setOrders((responseData.data || []).map(toRow));
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error(error);
			setOrders([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getOrders();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationModel.page, paginationModel.pageSize]);

	// Order ID search is client-side over the loaded page — the backend
	// orders endpoint does not accept a search parameter.
	const visibleRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return orders;
		return orders.filter((r) => r.orderId.toLowerCase().includes(q));
	}, [orders, search]);

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		setPaginationModel({ page: model.page, pageSize: model.pageSize });
	};

	const handleRowClick = (params: GridRowParams) => {
		setOrderDetails(params.row as OrderRow);
	};

	if (orderData) {
		return <OrderDetails {...{ orderData, setOrderDetails }} />;
	}

	return (
		<Card className="my-3 p-3 md:p-5">
			<FaiTypography
				variant={"h4"}
				fontWeight={typography.fontWeightMedium}
				mb={"21px"}
			>
				Orders
			</FaiTypography>
			<FaiTypography variant={"p"} mb={"21px"}>
				View and manage all your orders here. You can track the status, update
				order details, and process shipments. Use the search option to quickly
				find a specific order.
			</FaiTypography>
			<Box className="flex flex-col md:flex-row justify-between items-center my-4 w-full gap-3">
				<SearchInput
					placeholder="Search By Order ID"
					value={search}
					onChange={(e: any) => setSearch(e.target.value)}
				/>
			</Box>
			<Box sx={{ mt: 2 }}>
				{loading ? (
					<Box className="flex justify-center p-6">
						<CircularProgress />
					</Box>
				) : (
					<FaiDataGrid
						rows={visibleRows}
						columns={dataTableData.columns}
						pagination={true}
						pageSize={paginationModel.pageSize}
						rowCount={rowCount}
						paginationMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onRowClick={handleRowClick}
					/>
				)}
			</Box>
		</Card>
	);
}

export default Orders;
